import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { toast } from "sonner";
import { Home, CreditCard, AlertCircle, LogOut, UserPlus, Bell, FileText, Wallet } from "lucide-react";
import { useAuth } from "../context/AuthContext";

// Tenant PWA (/t) — mobile-first resident app.
// Shows dues, invoices, out-pass, pre-approved visitors, documents, referral.
export default function TenantPWA() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [me, setMe] = useState(null);
  const [tab, setTab] = useState("home");
  const [outpassForm, setOutpassForm] = useState({ from_at: "", expected_return_at: "", reason: "" });
  const [visitorForm, setVisitorForm] = useState({ visitor_name: "", visitor_phone: "", valid_from: "", valid_to: "" });
  const [preapproved, setPreapproved] = useState([]);

  const load = async () => {
    try { const { data } = await api.get("/pms/tenant/me"); setMe(data); }
    catch { setMe(null); }
    try { const { data } = await api.get("/pms/preapproved"); setPreapproved(data || []); }
    catch { setPreapproved([]); }
  };

  useEffect(() => {
    if (loading) return;
    if (!user) { nav("/login"); return; }
    load();
  }, [user, loading, nav]);

  if (loading || !me) return <div className="min-h-screen grid place-items-center text-white/60" style={{background:"#0D1B2A"}}>Loading...</div>;

  const submitOutpass = async (e) => {
    e.preventDefault();
    try { await api.post("/pms/outpass/request", outpassForm); toast.success("Out-pass requested"); setOutpassForm({from_at:"", expected_return_at:"", reason:""}); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };
  const submitVisitor = async (e) => {
    e.preventDefault();
    try { await api.post("/pms/preapproved", visitorForm); toast.success("Visitor pre-approved · QR generated"); setVisitorForm({visitor_name:"",visitor_phone:"",valid_from:"",valid_to:""}); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const inputCls = "w-full px-3 py-2.5 rounded-xl bg-white/5 border border-white/15 text-white text-sm focus:border-[#34C97B] focus:ring-2 focus:ring-[#34C97B]/30 outline-none";

  return (
    <div className="min-h-screen text-white pb-24" style={{background:"#0D1B2A", fontFamily:"Inter, ui-sans-serif, system-ui"}} data-testid="tenant-pwa">
      {/* Header */}
      <div className="sticky top-0 z-10 backdrop-blur bg-[#0D1B2A]/80 border-b border-white/10 px-4 py-3">
        <div className="max-w-md mx-auto flex items-center justify-between">
          <div>
            <div className="text-xs text-white/50">Hi, welcome back 👋</div>
            <div className="font-bold text-white">{me.tenant?.name || user.name}</div>
          </div>
          <Link to="/logout" className="text-white/60"><LogOut size={18}/></Link>
        </div>
      </div>

      <div className="max-w-md mx-auto px-4 pt-5">
        {!me.tenant ? (
          <div className="rounded-2xl p-6 border border-[#34C97B]/30 bg-white/[0.04] text-center">
            <Home size={40} className="text-[#34C97B] mx-auto mb-3"/>
            <div className="font-bold text-lg">No active tenancy</div>
            <p className="text-sm text-white/60 mt-1">Your owner will connect your account to a bed. Ask them to add you from their PMS.</p>
          </div>
        ) : (
          <>
            {/* Dues card */}
            <div className="rounded-2xl p-5 border border-[#34C97B]/40 mb-4 relative overflow-hidden" style={{background:"linear-gradient(135deg, rgba(52,201,123,0.20), rgba(52,201,123,0.04))"}} data-testid="dues-card">
              <div className="text-xs uppercase tracking-widest text-white/70">Total dues</div>
              <div className="text-4xl font-black text-white mt-1">₹{(me.dues||0).toLocaleString()}</div>
              {me.next_invoice ? (
                <div className="text-sm text-white/70 mt-1">Next invoice · {me.next_invoice.invoice_no}</div>
              ) : (
                <div className="text-sm text-[#34C97B] mt-1 flex items-center gap-1"><CreditCard size={14}/> All caught up!</div>
              )}
              {me.dues > 0 && (
                <button className="mt-4 w-full bg-white text-[#0D1B2A] font-bold py-3 rounded-xl hover:bg-white/90 transition" data-testid="pay-now-btn">
                  Pay Now via Razorpay
                </button>
              )}
            </div>

            {/* Tab bar (top scroll) */}
            <div className="flex gap-1 p-1 bg-white/5 border border-white/10 rounded-xl mb-4 overflow-x-auto">
              {[["home","Home"],["invoices","Invoices"],["outpass","Out-pass"],["visitor","Visitors"],["docs","Docs"]].map(([v,l]) => (
                <button key={v} onClick={()=>setTab(v)} className={`flex-1 text-xs font-semibold py-2 px-2 rounded-lg whitespace-nowrap ${tab===v?"bg-[#34C97B] text-white shadow":"text-white/60"}`} data-testid={`tab-${v}`}>{l}</button>
              ))}
            </div>

            {tab === "home" && (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <button onClick={()=>setTab("outpass")} className="rounded-2xl p-4 bg-white/[0.04] border border-white/10 text-left hover:border-[#34C97B]/50 transition" data-testid="qa-outpass">
                    <AlertCircle size={20} className="text-amber-300"/><div className="font-bold text-sm mt-2">Request Out-pass</div><div className="text-xs text-white/50">Overnight leave</div>
                  </button>
                  <button onClick={()=>setTab("visitor")} className="rounded-2xl p-4 bg-white/[0.04] border border-white/10 text-left hover:border-[#34C97B]/50 transition" data-testid="qa-visitor">
                    <UserPlus size={20} className="text-blue-300"/><div className="font-bold text-sm mt-2">Pre-approve visitor</div><div className="text-xs text-white/50">Generate QR</div>
                  </button>
                  <button onClick={()=>toast("Feature coming soon")} className="rounded-2xl p-4 bg-white/[0.04] border border-white/10 text-left hover:border-[#34C97B]/50 transition" data-testid="qa-complaint">
                    <Bell size={20} className="text-rose-300"/><div className="font-bold text-sm mt-2">Raise complaint</div><div className="text-xs text-white/50">Maintenance</div>
                  </button>
                  <button onClick={()=>navigator.clipboard?.writeText(`https://hostro.in/?ref=${me.tenant.id}`).then(()=>toast.success("Referral link copied · Refer & save ₹500"))} className="rounded-2xl p-4 bg-white/[0.04] border border-white/10 text-left hover:border-[#34C97B]/50 transition" data-testid="qa-refer">
                    <Wallet size={20} className="text-[#34C97B]"/><div className="font-bold text-sm mt-2">Refer friend</div><div className="text-xs text-white/50">Get ₹500 off</div>
                  </button>
                </div>
              </div>
            )}

            {tab === "invoices" && (
              <div className="space-y-2" data-testid="invoices-list">
                {(me.invoices||[]).length === 0 && <div className="text-sm text-white/50 text-center py-8">No invoices yet</div>}
                {(me.invoices||[]).map(i => (
                  <div key={i.id} className="rounded-xl p-3 bg-white/5 border border-white/10 flex items-center gap-3">
                    <div className={`w-9 h-9 rounded-full grid place-items-center ${i.status==="paid"?"bg-[#34C97B]/15 text-[#34C97B]":"bg-amber-500/15 text-amber-300"}`}>
                      <FileText size={16}/>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold truncate">{i.invoice_no}</div>
                      <div className="text-[11px] text-white/50">{i.period_start?.slice(0,10)} – {i.period_end?.slice(0,10)}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-bold">₹{(i.total||0).toLocaleString()}</div>
                      <div className={`text-[10px] uppercase ${i.status==="paid"?"text-[#34C97B]":"text-amber-300"}`}>{i.status}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {tab === "outpass" && (
              <div className="space-y-4" data-testid="outpass-tab">
                <form onSubmit={submitOutpass} className="rounded-2xl p-4 bg-white/[0.04] border border-white/10 space-y-3">
                  <div className="font-bold">Request Out-pass</div>
                  <input required type="datetime-local" className={inputCls} value={outpassForm.from_at} onChange={(e)=>setOutpassForm({...outpassForm, from_at:e.target.value})} data-testid="op-from"/>
                  <input required type="datetime-local" className={inputCls} value={outpassForm.expected_return_at} onChange={(e)=>setOutpassForm({...outpassForm, expected_return_at:e.target.value})} data-testid="op-to"/>
                  <input required className={inputCls} placeholder="Reason (e.g. home visit)" value={outpassForm.reason} onChange={(e)=>setOutpassForm({...outpassForm, reason:e.target.value})} data-testid="op-reason"/>
                  <button className="w-full bg-[#34C97B] hover:bg-[#22B36A] text-white font-bold py-3 rounded-xl transition" data-testid="op-submit">Request</button>
                </form>
                <div className="space-y-2">
                  {(me.outpasses||[]).map(o => (
                    <div key={o.id} className="rounded-xl p-3 bg-white/5 border border-white/10 flex items-center gap-3">
                      <div className={`w-9 h-9 rounded-full grid place-items-center ${o.status==="approved"?"bg-[#34C97B]/15 text-[#34C97B]":"bg-amber-500/15 text-amber-300"}`}>
                        <AlertCircle size={16}/>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold truncate">{o.reason}</div>
                        <div className="text-[11px] text-white/50">QR: {o.qr_token} · {o.status}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {tab === "visitor" && (
              <div className="space-y-4" data-testid="visitor-tab">
                <form onSubmit={submitVisitor} className="rounded-2xl p-4 bg-white/[0.04] border border-white/10 space-y-3">
                  <div className="font-bold">Pre-approve a visitor</div>
                  <input required className={inputCls} placeholder="Visitor name" value={visitorForm.visitor_name} onChange={(e)=>setVisitorForm({...visitorForm, visitor_name:e.target.value})} data-testid="pv-name"/>
                  <input required className={inputCls} placeholder="Visitor phone" value={visitorForm.visitor_phone} onChange={(e)=>setVisitorForm({...visitorForm, visitor_phone:e.target.value})} data-testid="pv-phone"/>
                  <input required type="date" className={inputCls} value={visitorForm.valid_from} onChange={(e)=>setVisitorForm({...visitorForm, valid_from:e.target.value})} data-testid="pv-from"/>
                  <input required type="date" className={inputCls} value={visitorForm.valid_to} onChange={(e)=>setVisitorForm({...visitorForm, valid_to:e.target.value})} data-testid="pv-to"/>
                  <button className="w-full bg-[#34C97B] hover:bg-[#22B36A] text-white font-bold py-3 rounded-xl transition" data-testid="pv-submit">Generate QR</button>
                </form>
                <div className="space-y-2">
                  {preapproved.map(v => (
                    <div key={v.id} className="rounded-xl p-3 bg-white/5 border border-white/10">
                      <div className="flex items-center justify-between">
                        <div className="text-sm font-semibold">{v.visitor_name}</div>
                        <span className="text-[10px] uppercase text-[#34C97B] tracking-wider">QR {v.qr_token}</span>
                      </div>
                      <div className="text-[11px] text-white/50">{v.visitor_phone} · Valid {v.valid_from} → {v.valid_to}</div>
                    </div>
                  ))}
                  {preapproved.length === 0 && <div className="text-sm text-white/50 text-center py-6">No pre-approved visitors</div>}
                </div>
              </div>
            )}

            {tab === "docs" && (
              <div className="rounded-2xl p-6 bg-white/[0.04] border border-white/10 text-center" data-testid="docs-tab">
                <FileText size={32} className="text-[#34C97B] mx-auto mb-3"/>
                <div className="font-bold">Your documents</div>
                <div className="text-xs text-white/50 mt-1">Agreement, KYC, receipts — coming soon</div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
